import _ from 'lodash';

import { statuses } from '../../../constants/project';
import {
  getLiveStreams,
  getSelectedLiveStream,
} from '../../../selectors/live-streams/live-streams';
import { getSelectedOrganizationId } from '../../../selectors/organizations/organizations';
import { mediaPlatformProvider } from '../../../transport/media-platform-provider';
import { RouterContext } from '../../../types/router';
import { getDefaultProject } from '../../projects/projects.selectors';
import { Dispatch } from '../../redux.types';
import {
  getLiveStreamAnalytics as getLiveStreamAnalyticsAction,
  getLiveStreams as getLiveStreamsAction,
} from '../live-streams';

import { getProjectsData } from './projects';
import { buildLoadDataCachedFn } from './utils/utils';

const loadLiveStreamsList = (
  routerContext: RouterContext,
  dispatch: Dispatch,
  getState,
) => {
  const state = getState();
  const organizationId = getSelectedOrganizationId(state, {
    router: routerContext,
  });

  if (!organizationId) {
    return Promise.reject({
      code: 404,
    });
  }

  return dispatch(getProjectsData(routerContext)).then((): any => {
    const updatedState = getState();
    const project = getDefaultProject(updatedState, {
      router: routerContext,
    });

    if (!project || project.status !== statuses.READY) {
      return null;
    }

    return mediaPlatformProvider
      .load(organizationId, project)
      .then(() =>
        getLiveStreamsAction({ organizationId, project })(dispatch, getState),
      );
  });
};

const loadLiveStreamAnalytics = (
  routerContext: RouterContext,
  dispatch: Dispatch,
  getState,
) => {
  const state = getState();
  const organizationId = getSelectedOrganizationId(state, {
    router: routerContext,
  });
  if (!organizationId) {
    return Promise.reject({
      code: 404,
    });
  }

  return dispatch(getProjectsData(routerContext)).then((): any => {
    const updatedState = getState();
    const project = getDefaultProject(updatedState, {
      router: routerContext,
    });

    if (!project || project.status !== statuses.READY) {
      return null;
    }

    const selectedLiveStream = getSelectedLiveStream(state);
    if (selectedLiveStream) {
      const liveStreamId = selectedLiveStream.id as string;
      return mediaPlatformProvider
        .load(organizationId, project)
        .then(() =>
          getLiveStreamAnalyticsAction(
            { organizationId, project },
            liveStreamId,
          )(dispatch, getState),
        );
    }
  });
};

const fetchDataIfNoLiveStreamIdOrNoLiveStreams = (
  prevState?,
  nextState?,
  prevRouter?,
  nextRouter?,
) => {
  const liveStreams = getLiveStreams(nextState);
  return !_.get(nextRouter, ['params', 'liveStreamId']) || !liveStreams.length;
};

const loadDataCached = buildLoadDataCachedFn(
  loadLiveStreamsList,
  fetchDataIfNoLiveStreamIdOrNoLiveStreams,
);

// load live streams data
export const getLiveStreamsData =
  (context) => (dispatch: Dispatch, getState) => {
    return loadDataCached(context, dispatch, getState);
  };

// load live stream analytics data
export const getLiveStreamAnalyticsData =
  (context) => (dispatch: Dispatch, getState) => {
    return loadLiveStreamAnalytics(context, dispatch, getState);
  };
